import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { CheckCircle, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency as formatCurrencyFn } from '../utils/formatters';

const SwipeableInvoiceCard = ({ 
  invoice, 
  onView, 
  onMarkPaid, 
  onShare,
  statusColors 
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userCountry = user?.companyInfo?.country;
  const userCurrency = user?.companyInfo?.defaultCurrency || 'COP';
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isRevealed, setIsRevealed] = useState(false);

  // Las cotizaciones, ofertas, pedidos y notas de entrega son documentos comerciales
  // no cobrables: NUNCA cambian de status automáticamente y siempre se muestran
  // en amarillo (pendiente) por consistencia visual.
  const isQuotation =
    invoice.documentType === 'quotation' ||
    invoice.documentType === 'offer' ||
    invoice.documentType === 'order' ||
    invoice.documentType === 'delivery_note' ||
    invoice.number?.startsWith('COT') ||
    invoice.number?.startsWith('OFE') ||
    invoice.number?.startsWith('PED') ||
    invoice.number?.startsWith('NEN');

  const SWIPE_THRESHOLD = 80;
  const MAX_SWIPE = 90;

  const handlers = useSwipeable({
    onSwiping: (e) => {
      // No permitir swipe en cotizaciones
      if (isQuotation) return;
      
      // Only allow horizontal swipe if it's clearly horizontal (not vertical scroll)
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) * 2) {
        if (isRevealed) {
          // Only allow left swipe to close
          const offset = Math.max(0, Math.min(MAX_SWIPE, MAX_SWIPE + e.deltaX));
          setSwipeOffset(offset);
        } else {
          // Only allow right swipe (positive deltaX)
          if (e.deltaX > 0) {
            const offset = Math.min(MAX_SWIPE, e.deltaX);
            setSwipeOffset(offset);
          }
        }
      }
    },
    onSwipedLeft: (e) => {
      // Close if revealed
      if (isRevealed) {
        setSwipeOffset(0);
        setIsRevealed(false);
      }
    },
    onSwipedRight: (e) => {
      // No permitir swipe en cotizaciones
      if (isQuotation) return;
      
      // Only open if swipe is strong enough
      if (!isRevealed && Math.abs(e.deltaX) > Math.abs(e.deltaY) * 1.5 && Math.abs(e.deltaX) > SWIPE_THRESHOLD) {
        setIsRevealed(true);
        setSwipeOffset(MAX_SWIPE);
      } else {
        setSwipeOffset(0);
        setIsRevealed(false);
      }
    },
    onTap: () => {
      if (isRevealed && !isQuotation) {
        // Execute paid action when tapped while revealed
        handleActionClick('paid');
      } else {
        // Navigate to invoice detail page on mobile
        navigate(`/invoice/${invoice.id}`);
      }
    },
    trackMouse: false,
    trackTouch: true,
    preventScrollOnSwipe: false, // Allow vertical scroll
    delta: 20, // Increased minimum distance before detecting swipe
  });

  const handleActionClick = (action, e) => {
    if (e) e.stopPropagation();
    if (action === 'paid') {
      // Toggle: if paid -> pending, if pending -> paid
      const newStatus = invoice.status === 'paid' ? 'pending' : 'paid';
      onMarkPaid(invoice.id, newStatus);
    }
    // Reset swipe after action
    setTimeout(() => {
      setSwipeOffset(0);
      setIsRevealed(false);
    }, 200);
  };

  const resetSwipe = () => {
    setSwipeOffset(0);
    setIsRevealed(false);
  };

  const getStatusColor = (status) => {
    // Las cotizaciones (COT-) NUNCA cambian de status, así que SIEMPRE se muestran
    // en amarillo (pendiente). Su status real en la base de datos es ignorado visualmente.
    if (isQuotation) {
      return 'bg-yellow-500';
    }
    switch (status) {
      case 'paid':
        return 'bg-green-500';
      case 'partial':
        return 'bg-blue-500';
      case 'pending':
        return 'bg-yellow-500';
      case 'overdue':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <div className="relative overflow-hidden bg-gray-50 dark:bg-gray-800 mb-1">
      {/* Background action - Toggle Paid Status (revealed when swiping RIGHT) */}
      <div className="absolute inset-0 pointer-events-none">
        <div 
          className={`w-full h-full flex items-center justify-start pl-4 pointer-events-auto ${invoice.status === 'paid' ? 'bg-yellow-500' : 'bg-green-500'}`}
          onClick={(e) => handleActionClick('paid', e)}
        >
          <div className="flex items-center gap-2 text-white font-medium">
            <CheckCircle className="w-5 h-5" />
            <span>{invoice.status === 'paid' ? 'No pagada' : 'Pagada'}</span>
          </div>
        </div>
      </div>

      {/* Main card content */}
      <div 
        {...handlers}
        onClick={(e) => {
          // Bloqueo total para cotizaciones/proformas: NUNCA cambian de status por tap
          if (isQuotation) {
            navigate(`/invoice/${invoice.id}`);
            return;
          }
          // If swipe is revealed and user clicks, execute the paid action
          if (isRevealed) {
            handleActionClick('paid', e);
            return;
          }
          // No swipe revealed, navigate to invoice detail
          if (swipeOffset === 0) {
            navigate(`/invoice/${invoice.id}`);
          }
        }}
        className="relative bg-white dark:bg-card flex items-center py-3 px-3 cursor-pointer border-b border-gray-100 dark:border-gray-700"
        style={{ 
          transform: `translateX(${swipeOffset}px)`,
          transition: swipeOffset === 0 ? 'transform 0.2s ease-out' : 'none'
        }}
        data-testid={`invoice-card-${invoice.id}`}
      >
        {/* Status indicator */}
        <div className={`w-3 h-3 rounded-full mr-3 flex-shrink-0 ${getStatusColor(invoice.status)}`} />
        
        {/* Invoice info - left side */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 dark:text-white truncate">
            {invoice.clientName}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {invoice.number}
          </p>
        </div>

        {/* Amount and date - right side */}
        <div className="text-right ml-3 flex-shrink-0">
          <p className="font-bold text-gray-900 dark:text-white">
            {formatCurrencyFn(invoice.total, { country: userCountry, minimumFractionDigits: 0, maximumFractionDigits: 0 })} {userCurrency}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {invoice.date}
          </p>
        </div>

        {/* Arrow indicator */}
        <ChevronRight className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" />
      </div>
    </div>
  );
};

export default SwipeableInvoiceCard;
